import React, { useState, useEffect, useRef } from 'react';
import * as Icons from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { useLocation } from 'react-router-dom';
import { walletService } from '../services/api';
import { computeCollectionAmounts } from '../utils/payments';
import MultiplePaymentEvidenceUpload from './MultiplePaymentEvidenceUpload';
import ReturnToBillingModal from './ReturnToBillingModal';
import MixturesValidationModal from './MixturesValidationModal';

// Zona de carga que permite: pegar (Ctrl+V), arrastrar y seleccionar archivo
// Sólo acepta imágenes; valida tamaño (<= 5MB) y muestra estado
const UploadDropzone = ({
  label = 'Comprobante (imagen) *',
  file,
  onFile,
  required = false,
  hint = 'Pega (Ctrl+V) o usa "Pegar desde portapapeles"; tambien puedes arrastrar o usar el boton "Seleccionar archivo"',
  autoFocus = true,
  onFocusExtra = null,
  showPasteButton = false,
  onPasteClick
}) => {
  const inputRef = useRef(null);
  const zoneRef = useRef(null);

  // Enfocar automáticamente la zona para habilitar pegado inmediato (Ctrl+V)
  useEffect(() => {
    if (autoFocus && zoneRef.current) {
      const id = setTimeout(() => {
        try { zoneRef.current.focus(); } catch (_) { }
      }, 120);
      return () => clearTimeout(id);
    }
  }, [autoFocus]);

  const validateAndEmit = (f) => {
    if (!f) return;
    if (!f.type || !f.type.startsWith('image/')) {
      toast.error('Solo se permiten archivos de imagen');
      return;
    }
    if (f.size > 5 * 1024 * 1024) {
      toast.error('El archivo no puede ser mayor a 5MB');
      return;
    }
    onFile(f);
  };

  const onInputChange = (e) => {
    const f = e.target.files?.[0];
    validateAndEmit(f);
  };



  const onDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer?.files?.[0];
    validateAndEmit(f);
  };

  const onDragOver = (e) => e.preventDefault();

  return (
    <div className="w-full">
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {label}
      </label>
      <div
        ref={zoneRef}
        tabIndex={0}
        contentEditable
        suppressContentEditableWarning
        spellCheck={false}
        onInput={(e) => { try { e.currentTarget.textContent = ''; } catch (_) { } }}
        onDrop={onDrop}
        onDragOver={onDragOver}
        onFocus={(e) => {
          // Opcional: al enfocar, seleccionar visualmente para indicar que ya puedes pegar
          e.currentTarget.classList.add('ring-2', 'ring-blue-500');
          if (typeof onFocusExtra === 'function') onFocusExtra();
        }}
        onBlur={(e) => {
          e.currentTarget.classList.remove('ring-2', 'ring-blue-500');
        }}
        className="flex flex-col items-center justify-center w-full px-3 py-6 border-2 border-dashed rounded-md cursor-pointer bg-white hover:bg-gray-50 border-gray-300 focus:outline-none"
        role="button"
        aria-label={label}
        style={{ caretColor: 'transparent' }}
      >
        <Icons.Upload className="w-6 h-6 text-gray-500 mb-2" />
        <p className="text-sm text-gray-700 text-center">
          {hint}
        </p>
        {showPasteButton && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onPasteClick && onPasteClick(e); }}
            className="mt-2 text-xs text-blue-600 hover:underline focus:outline-none"
          >
            Pegar desde portapapeles
          </button>
        )}
        {file && (
          <p className="text-xs text-green-600 mt-2">Archivo: {file.name}</p>
        )}
      </div>
      <div className="mt-2">
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="text-xs px-2 py-1 border rounded text-gray-700 hover:bg-gray-50"
        >
          Seleccionar archivo
        </button>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        onChange={onInputChange}
        className="hidden"
        aria-hidden="true"
        {...(required ? { required: true } : {})}
      />
    </div>
  );
};

const WalletValidationModal = ({ isOpen, onClose, order, onValidate, cashSessionOpen = true }) => {
  const { user } = useAuth();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const currentView = queryParams.get('view');
  const [loading, setLoading] = useState(false);
  // Zona activa para pegado global: 'transfer' o 'cash' (Aunque ahora se usa MultiplePaymentE... que maneja su propio paste)
  const [activeDropzone, setActiveDropzone] = useState(null);
  const [sessionUploads, setSessionUploads] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);

  // --- Estados para Conciliación Online (Opcional) ---
  const [pendingMovements, setPendingMovements] = useState([]);
  const [loadingMovements, setLoadingMovements] = useState(false);
  const [isFetchingMovements, setIsFetchingMovements] = useState(false); // Prevent duplicate requests
  const [movementSearch, setMovementSearch] = useState('');
  const [linkedMovements, setLinkedMovements] = useState([]);  // CAMBIO: Array para múltiples vínculos
  const [showReconciliation, setShowReconciliation] = useState(false);
  const [alreadyLinked, setAlreadyLinked] = useState([]); // Pagos ya vinculados anteriormente

  // --- Estado para Modal de Devolución a Facturación ---
  const [showReturnToBilling, setShowReturnToBilling] = useState(false);

  // --- Estado para Validación de Mezclas ---
  const [showMixturesValidation, setShowMixturesValidation] = useState(false);
  const [mixturesValidationResult, setMixturesValidationResult] = useState(null);
  const [validationBlocked, setValidationBlocked] = useState(false);



  const formatDate = (dateString) => {
    if (!dateString) return '';
    const d = new Date(dateString);
    // Ajustar zona horaria si es necesario o usar UTC si viene del back como tal.
    // Asumimos string ISO. Mejor usar slice para YYYY-MM-DD si coincide.
    if (dateString.includes('T')) return dateString.split('T')[0];
    return d.toLocaleDateString('es-CO', { year: 'numeric', month: '2-digit', day: '2-digit' });
  };

  // Cargar movimientos pendientes al abrir modal si es transferencia
  useEffect(() => {
    if (isOpen && order) {
      const pm = normalizePaymentMethod(order.payment_method);
      if (pm === 'transferencia') {
        fetchPendingMovements();
        setShowReconciliation(true); // Mostrar por defecto para incentivar uso
      } else {
        setShowReconciliation(false);
      }
      setLinkedMovements([]); // Reset al abrir - CAMBIO: Array vacío
      setMovementSearch('');

      // Cargar pagos ya vinculados
      fetchAlreadyLinked(order.id);

      // Mixtures validation DISABLED in Cartera modal - only in Facturación modal
      // setShowMixturesValidation(true);
      setValidationBlocked(false); // Esta línea es necesaria para el flujo normal
      setMixturesValidationResult(null);
    }
  }, [isOpen, order?.id]); // order.id stable

  const fetchAlreadyLinked = async (orderId) => {
    try {
      const res = await walletService.getLinkedPayments(orderId);
      // Ensure we get an array
      const list = Array.isArray(res) ? res : (res.data || []);
      setAlreadyLinked(list);
    } catch (error) {
      console.error('Error fetching linked payments:', error);
    }
  };

  // Fetch Existing Payment Evidences
  const [existingEvidences, setExistingEvidences] = useState([]);
  const [previewImage, setPreviewImage] = useState(null);
  const [isZoomed, setIsZoomed] = useState(false);
  const [imageLoadError, setImageLoadError] = useState(null); // INFO: Debug state for image failure

  useEffect(() => {
    setImageLoadError(null); // Reset error on image change
  }, [previewImage]);

  useEffect(() => {
    if (isOpen && order?.id) {
      setPreviewImage(null); // Reset preview on order change
      setExistingEvidences([]); // Clean stale evidences immediately
      walletService.getPaymentEvidences(order.id)
        .then(response => {
          const list = Array.isArray(response) ? response : (response.data || []);
          const urls = list.map(e => e.file_path);
          setExistingEvidences(urls);
        })
        .catch(err => {
          console.error('Error loading evidences', err);
        });
    }
  }, [isOpen, order?.id]);




  const fetchPendingMovements = async () => {
    // Prevent duplicate concurrent requests
    if (isFetchingMovements) {
      console.log('[FETCH] Already fetching movements, skipping duplicate request');
      return;
    }

    try {
      setIsFetchingMovements(true);
      setLoadingMovements(true);
      const res = await walletService.getBancolombiaMovements({
        status: 'pending',
        limit: 300,
        _t: Date.now() // Cache buster
      });
      const data = res.data?.data || res.data || [];
      const list = Array.isArray(data) ? data : (data.movements || []);

      // ENHANCED FILTER: Exclude movements that already have pedido_id (already linked)
      const filtered = list.filter(movement => {
        // CRITICAL: Movement must NOT have pedido_id (already linked to an order)
        if (movement.pedido_id !== null && movement.pedido_id !== undefined) {
          console.log(`[FILTER] Excluding movement ${movement.id} - already linked to order ${movement.pedido_id}`);
          return false;
        }

        // Must be income (positive value)
        if (movement.valor <= 0) {
          console.log(`[FILTER] Excluding movement ${movement.id} - non-positive value: ${movement.valor}`);
          return false;
        }

        return true;
      });

      console.log(`📊 [MOVEMENTS] Total: ${list.length}, Filtered: ${filtered.length}`);
      setPendingMovements(filtered);
    } catch (err) {
      console.error('Error cargando movimientos bancarios:', err);
      setPendingMovements([]);
    } finally {
      setIsFetchingMovements(false);
      setLoadingMovements(false);
    }
  };

  // Auto-select first image if available
  useEffect(() => {
    // If user already selected one, don't overwrite
    if (previewImage) return;

    // 1. Try Existing Evidences (from payment_evidences table)
    if (existingEvidences && existingEvidences.length > 0) {
      setPreviewImage(existingEvidences[0]);
      return;
    }

    // 2. Try New Payment Evidence Path (from orders table - messenger flow)
    if (order && order.payment_evidence_path) {
      setPreviewImage(order.payment_evidence_path);
      return;
    }

    // 3. Try Order Files (Legacy)
    if (order && order.files && order.files.length > 0) {
      setPreviewImage(order.files[0]);
      return;
    }

    // 3. Try Form Data (if uploaded in session)
    if (formData.paymentProofImage instanceof File) {
      setPreviewImage(formData.paymentProofImage);
    }

  }, [existingEvidences, order, previewImage]);



  // Búsqueda en servidor para encontrar movimientos antiguos por monto exacto
  useEffect(() => {
    // Si ya estamos cargando o no hay búsqueda, salir
    const rawQuery = movementSearch || '';
    // Solo buscar si parece un monto (números) y tiene longitud decente
    const numericQuery = rawQuery.replace(/[^0-9]/g, '');

    if (!numericQuery || numericQuery.length < 4) return;

    // Debounce
    const timeoutId = setTimeout(async () => {
      try {
        setLoadingMovements(true);
        console.log('🔍 Buscando movimiento en servidor por monto:', numericQuery);
        // Buscar específicamente por ese monto en el servidor
        const res = await walletService.getBancolombiaMovements({
          status: 'pending',
          amount: numericQuery,
          limit: 50
        });

        const data = res.data?.data || res.data || [];
        const list = Array.isArray(data) ? data : (data.movements || []);

        if (list.length > 0) {
          setPendingMovements(prev => {
            // Unir y deduplicar por ID
            const existingIds = new Set(prev.map(m => m.id));

            // CRITICAL FIX: Also exclude movements matching already-registered payments
            const newItems = list.filter(movement => {
              if (existingIds.has(movement.id)) return false;
              if (movement.pedido_id) return false; // Already linked
              return true;
            });

            if (newItems.length > 0) {
              // Agregamos los encontrados al array existente
              return [...prev, ...newItems];
            }
            return prev;
          });
        }
      } catch (err) {
        console.error("Error searching movements on server", err);
      } finally {
        setLoadingMovements(false);
      }
    }, 800); // 800ms debounce

    // Mostrar hint visual si el usuario escribe pero no lo suficiente
    if (numericQuery.length > 0 && numericQuery.length < 4) {
      // Podríamos poner un estado de advertencia aquí si quisiéramos
    }

    return () => clearTimeout(timeoutId);
  }, [movementSearch]);

  // AUTO-SEARCH: Buscar automáticamente el monto PENDIENTE del pedido al abrir (si es transferencia)
  useEffect(() => {
    if (isOpen && order && (normalizePaymentMethod(order.payment_method) === 'transferencia' || normalizePaymentMethod(order.payment_method) === 'pago_electronico')) {
      // CRITICAL FIX: Calcular pending amount porque la columna pending_amount no existe
      // pending = total - paid
      const totalAmount = parseCurrency(order.total_amount);
      const paidAmount = parseCurrency(order.paid_amount || 0);
      const pendingAmount = totalAmount - paidAmount;

      if (pendingAmount > 0) {
        // Pequeño delay para no competir con el fetch inicial
        setTimeout(() => {
          console.log('🤖 Auto-searching movement for PENDING amount:', pendingAmount, '(total:', totalAmount, '- paid:', paidAmount, ')');
          walletService.getBancolombiaMovements({
            status: 'pending',
            amount: pendingAmount,
            amountTolerance: 5000, // Tolerancia de 5000 para redondeos
            limit: 50
          }).then(res => {
            const data = res.data?.data || res.data || [];
            const list = Array.isArray(data) ? data : (data.movements || []);
            if (list.length > 0) {
              setPendingMovements(prev => {
                const existingIds = new Set(prev.map(m => m.id));

                // CRITICAL FIX: Filter out movements that match already-registered payments
                // Compare by amount + date instead of IDs (different tables)
                const newItems = list.filter(movement => {
                  if (existingIds.has(movement.id)) return false;
                  if (movement.pedido_id) return false; // Already linked
                  return true;
                });

                if (newItems.length > 0) {
                  toast.success(`Encontramos ${newItems.length} movimientos coincidiendo con el valor pendiente ($${pendingAmount.toLocaleString()})`, { icon: '🔍' });
                  return [...prev, ...newItems];
                }
                return prev;
              });
            }
          }).catch(err => console.error('Auto-search failed', err));
        }, 500);
      }
    }
  }, [isOpen, order, alreadyLinked]); // Add alreadyLinked to re-filter when it changes

  // CRITICAL: Re-filter pending movements when alreadyLinked changes
  // This ensures that when payments are loaded from DB, they're removed from available movements
  useEffect(() => {
    if (alreadyLinked.length === 0) return; // No payments to filter out

    setPendingMovements(prev => {
      return prev.filter(movement => {
        const movementAmount = parseFloat(movement.valor);
        const movementDate = movement.fecha ? movement.fecha.slice(0, 10) : null;

        const isAlreadyRegistered = alreadyLinked.some(payment => {
          const paymentAmount = parseFloat(payment.reconciled_amount || payment.valor || 0);
          const paymentDate = payment.reconciled_at || payment.fecha;
          const paymentDateNormalized = paymentDate ? paymentDate.slice(0, 10) : null;

          const amountMatch = Math.abs(paymentAmount - movementAmount) < 0.01;
          const dateMatch = movementDate && paymentDateNormalized && movementDate === paymentDateNormalized;

          return amountMatch && dateMatch;
        });

        return !isAlreadyRegistered;
      });
    });
  }, [alreadyLinked]); // Re-run when alreadyLinked changes

  const handleSelectMovement = (movement) => {
    console.log('🔵 [handleSelectMovement] Called with:', movement);
    console.log('🔵 [handleSelectMovement] Current linkedMovements:', linkedMovements);

    // Prevenir duplicados en la sesión actual
    if (linkedMovements.some(m => m.id === movement.id)) {
      toast.error('Este movimiento ya está vinculado en esta sesión');
      return;
    }

    // CRITICAL FIX: Verificar que el movimiento no esté ya registrado como pago en la BD
    // Comparamos por monto y fecha para detectar pagos ya procesados
    const movementAmount = parseFloat(movement.valor);
    const movementDate = movement.fecha ? movement.fecha.slice(0, 10) : null;

    const isAlreadyPaid = alreadyLinked.some(payment => {
      const paymentAmount = parseFloat(payment.reconciled_amount || payment.valor || 0);
      const paymentDate = payment.reconciled_at || payment.fecha;
      const paymentDateNormalized = paymentDate ? paymentDate.slice(0, 10) : null;

      // Coincidencia por monto exacto y fecha
      const amountMatch = Math.abs(paymentAmount - movementAmount) < 0.01;
      const dateMatch = movementDate && paymentDateNormalized && movementDate === paymentDateNormalized;

      return amountMatch && dateMatch;
    });

    if (isAlreadyPaid) {
      toast.error('⚠️ Este pago ya fue registrado anteriormente. No puedes vincularlo nuevamente.', {
        duration: 5000,
        icon: '🚫',
        style: {
          background: '#FEE2E2',
          color: '#991B1B',
          border: '2px solid #DC2626'
        }
      });
      return;
    }

    // Agregar a la lista
    const newLinkedMovements = [...linkedMovements, movement];
    console.log('🔵 [handleSelectMovement] New linkedMovements:', newLinkedMovements);
    setLinkedMovements(newLinkedMovements);

    // Calcular total acumulado
    const totalAmount = newLinkedMovements.reduce((sum, m) => sum + m.valor, 0);

    // Autocompletar formulario (fecha y banco) - monto se gestiona por useEffect
    setFormData(prev => ({
      ...prev,
      paymentDate: movement.fecha ? movement.fecha.slice(0, 10) : prev.paymentDate,
      bankName: 'bancolombia'
    }));

    // Limpiar campo de búsqueda para permitir buscar siguiente movimiento
    setMovementSearch('');

    console.log('[DEBUG] Monto actualizado:', totalAmount, 'Total movimientos:', newLinkedMovements.length);

    toast.success(`Movimiento ${newLinkedMovements.length} agregado. Total: ${formatAmount(totalAmount)}`);
  };

  const handleRemoveMovement = (index) => {
    const newLinkedMovements = linkedMovements.filter((_, idx) => idx !== index);
    setLinkedMovements(newLinkedMovements);

    // Recalcular total
    const totalAmount = newLinkedMovements.reduce((sum, m) => sum + m.valor, 0);
    setFormData(prev => ({
      ...prev,
      paymentAmount: totalAmount || ''
    }));

    toast('Movimiento eliminado', { icon: '🗑️' });
  };


  const modalRef = useRef(null);
  const pasteCatcherRef = useRef(null);
  // Evitar duplicados de pegado (varios listeners pueden dispararse)
  const pasteGuardRef = useRef(0);
  const recentlyHandledPaste = () => (Date.now() - pasteGuardRef.current) < 2000;
  const markPasteHandled = () => { pasteGuardRef.current = Date.now(); };
  // Guard adicional para notificación visual única
  const lastToastRef = useRef(0);
  const showUniquePasteToast = () => {
    const now = Date.now();
    // Coalescar múltiples llamadas en el mismo tick (StrictMode/HMR/ráfagas)
    if (typeof window !== 'undefined') {
      if (window.__walletPasteToastScheduled) return;
      window.__walletPasteToastScheduled = true;
      setTimeout(() => {
        try {
          window.__walletPasteToastScheduled = false;
          if (window.__walletPasteToastShownAt && (Date.now() - window.__walletPasteToastShownAt) < 3000) {
            return;
          }
          window.__walletPasteToastShownAt = Date.now();
          toast.success('Imagen pegada desde el portapapeles', { duration: 2500 });
        } catch (_) {
          // fallback sin window
          if ((Date.now() - lastToastRef.current) >= 3000) {
            lastToastRef.current = Date.now();
            toast.success('Imagen pegada desde el portapapeles', { duration: 2500 });
          }
        }
      }, 0);
      return;
    }

    // Fallback no-window
    if ((now - lastToastRef.current) >= 3000) {
      lastToastRef.current = now;
      toast.success('Imagen pegada desde el portapapeles', { duration: 2500 });
    }
    // Fallback no-window: si window no está disponible (SSR), usar fallback simple
    if ((now - lastToastRef.current) >= 3000) {
      lastToastRef.current = now;
      toast.success('Imagen pegada desde el portapapeles', { duration: 2500 });
    }
  };

  // Asegurar foco del modal y del catcher oculto para habilitar Ctrl+V sin clic
  useEffect(() => {
    if (isOpen) {
      // Intentar enfocar el modal principal
      const idA = setTimeout(() => {
        try { modalRef.current && modalRef.current.focus(); } catch (_) { }
      }, 50);

      // Enfocar el PasteCatcher oculto para interceptar Ctrl+V globalmente si nada más tiene foco
      const idB = setTimeout(() => {
        try {
          if (document.activeElement === document.body) {
            pasteCatcherRef.current && pasteCatcherRef.current.focus();
          }
        } catch (_) { }
      }, 150);

      return () => { clearTimeout(idA); clearTimeout(idB); };
    }
  }, [isOpen]);

  // Listener GLOBAL de paste cuando el modal está abierto
  useEffect(() => {
    if (!isOpen) return;

    const handleGlobalPaste = (e) => {
      // Si el foco está en un input/textarea, no interceptar (dejar comportamiento nativo)
      if (['INPUT', 'TEXTAREA'].includes(document.activeElement?.tagName)) return;

      // Si ya se manejó, salir
      if (recentlyHandledPaste()) return;

      handleClipboardImage(e);
    };

    window.addEventListener('paste', handleGlobalPaste);
    return () => window.removeEventListener('paste', handleGlobalPaste);
  }, [isOpen]);


  // Helper: intenta extraer una imagen del portapapeles (archivo, items, dataURL o URL http) y adjuntarla
  const handleClipboardImage = async (e) => {
    try {
      const dt = e?.clipboardData;
      let file = null;

      // 1) Si vienen archivos directos
      if (dt && dt.files && dt.files.length) {
        const f0 = dt.files[0];
        if (f0 && f0.type && f0.type.startsWith('image/')) {
          file = f0;
        }
      }

      // 2) Si vienen items
      if (!file && dt && dt.items && dt.items.length) {
        for (const it of dt.items) {
          if (it.type && it.type.startsWith('image/')) {
            const blob = it.getAsFile();
            if (blob) {
              const ext = blob.type.includes('jpeg') ? 'jpg' : (blob.type.split('/')[1] || 'png');
              file = new File([blob], `comprobante-${Date.now()}.${ext}`, { type: blob.type || 'image/png' });
              break;
            }
          }
        }
      }

      // 3) Si viene texto: dataURL o URL de imagen
      if (!file && dt) {
        const txt = dt.getData && dt.getData('text/plain');
        if (txt && typeof txt === 'string' && txt.length) {
          if (txt.startsWith('data:image/')) {
            // dataURL -> Blob
            const arr = txt.split(',');
            const mime = arr[0].match(/:(.*?);/)[1] || 'image/png';
            const bstr = atob(arr[1]);
            let n = bstr.length; const u8 = new Uint8Array(n);
            while (n--) u8[n] = bstr.charCodeAt(n);
            const blob = new Blob([u8], { type: mime });
            const ext = mime.includes('jpeg') ? 'jpg' : (mime.split('/')[1] || 'png');
            file = new File([blob], `comprobante-${Date.now()}.${ext}`, { type: mime });
          } else if (/^https?:.*\.(png|jpe?g|gif|webp)(\?.*)?$/i.test(txt)) {
            try {
              const res = await fetch(txt, { mode: 'cors' });
              const blob = await res.blob();
              if (blob && blob.type && blob.type.startsWith('image/')) {
                const ext = blob.type.includes('jpeg') ? 'jpg' : (blob.type.split('/')[1] || 'png');
                file = new File([blob], `comprobante-${Date.now()}.${ext}`, { type: blob.type || 'image/png' });
              }
            } catch (_) { /* noop */ }
          }
        }
      }

      if (file) {
        if (recentlyHandledPaste()) {
          if (e && typeof e.preventDefault === 'function') e.preventDefault();
          return true;
        }
        setFormData(prev => ({
          ...prev,
          paymentProofImage: file
        }));
        markPasteHandled();
        showUniquePasteToast();
        if (e && typeof e.preventDefault === 'function') e.preventDefault();
        return true;
      }
    } catch (_) { /* noop */ }
    return false;
  };

  // Intento usando Clipboard API (requiere gesto de usuario; capturamos Ctrl+V en keydown)
  const handleClipboardReadViaAPI = async (e) => {
    if (!navigator.clipboard || !navigator.clipboard.read) return false;
    try {
      const items = await navigator.clipboard.read();
      for (const item of items) {
        const types = item.types || [];
        const imgType = types.find((t) => t.startsWith('image/'));
        if (imgType) {
          const blob = await item.getType(imgType);
          if (blob) {
            const ext = blob.type.includes('jpeg') ? 'jpg' : (blob.type.split('/')[1] || 'png');
            const file = new File([blob], `comprobante-${Date.now()}.${ext}`, { type: blob.type || 'image/png' });
            if (recentlyHandledPaste()) {
              if (e && typeof e.preventDefault === 'function') e.preventDefault();
              return true;
            }
            setFormData(prev => ({
              ...prev,
              paymentProofImage: file
            }));
            markPasteHandled();
            showUniquePasteToast();
            if (e && typeof e.preventDefault === 'function') e.preventDefault();
            return true;
          }
        }
      }
    } catch (err) {
      console.warn('navigator.clipboard.read() falló', err);
    }
    return false;
  };





  // Listener único en captura (window) para maximizar compatibilidad y evitar duplicados
  useEffect(() => {
    if (!isOpen) {
      // Si cerramos, quitar cualquier handler global previo
      if (typeof window !== 'undefined' && window.__walletPasteHandler) {
        try { window.removeEventListener('paste', window.__walletPasteHandler, true); } catch (_) { }
        window.__walletPasteHandler = null;
      }
      return;
    }

    // Antes de agregar, eliminar cualquier handler colgado por HMR/StrictMode
    if (typeof window !== 'undefined' && window.__walletPasteHandler) {
      try { window.removeEventListener('paste', window.__walletPasteHandler, true); } catch (_) { }
      window.__walletPasteHandler = null;
    }

    const handlePasteCapture = async (e) => {
      // Guard instantáneo por ráfagas (múltiples listeners o múltiples eventos del mismo pegado)
      if (typeof window !== 'undefined') {
        if (window.__walletPasteInFlight) {
          try { e.preventDefault(); e.stopPropagation(); } catch (_) { }
          return;
        }
        window.__walletPasteInFlight = true;
        setTimeout(() => { try { window.__walletPasteInFlight = false; } catch (_) { } }, 150);
      }

      if (recentlyHandledPaste()) {
        try { e.preventDefault(); e.stopPropagation(); } catch (_) { }
        return;
      }
      const ok = await handleClipboardImage(e);
      if (ok) {
        try { e.preventDefault(); e.stopPropagation(); } catch (_) { }
      }
    };

    // if (typeof window !== 'undefined') {
    //   window.__walletPasteHandler = handlePasteCapture;
    //   window.addEventListener('paste', handlePasteCapture, true);
    // }

    // return () => {
    //   if (typeof window !== 'undefined' && window.__walletPasteHandler) {
    //     try { window.removeEventListener('paste', window.__walletPasteHandler, true); } catch (_) { }
    //     window.__walletPasteHandler = null;
    //   }
    // };
  }, [isOpen, activeDropzone]);


  // ===================================================================
  // WALLET MODAL: Direct calculation instead of computeCollectionAmounts
  // computeCollectionAmounts is designed for messengers and has complex
  // inference logic. For the wallet validation modal, we need a simple:
  // pendiente = total - pagado
  // ===================================================================

  // Robust parsing for order total (handling thousands separators)
  const parseCurrency = (val) => {
    if (typeof val === 'number') return val;
    if (!val) return 0;
    const s = String(val).trim();

    // Si tiene ambos, asumimos formato CO: 1.234,56
    if (s.includes('.') && s.includes(',')) {
      return parseFloat(s.replace(/\./g, '').replace(',', '.'));
    }

    // Si solo tiene punto: puede ser miles (1.234) o decimal (12.34)
    if (s.includes('.') && !s.includes(',')) {
      const parts = s.split('.');
      // Si hay más de un punto, definitivamente son miles
      if (parts.length > 2) return parseFloat(s.replace(/\./g, ''));

      const lastPart = parts[1];
      // Si la parte decimal tiene exactamente 3 dígitos, es MUY probable que sean miles (ej: 55.160)
      // Si tiene 2 o 1, es probable que sea decimal (ej: 55160.00)
      if (lastPart.length === 3) {
        return parseFloat(s.replace(/\./g, ''));
      }
      // De lo contrario, tratar como decimal estándar
      return parseFloat(s);
    }

    // Si solo tiene coma, tratar como decimal
    if (s.includes(',') && !s.includes('.')) {
      return parseFloat(s.replace(',', '.'));
    }

    return parseFloat(s);
  };
  const orderSiigoTotal = parseCurrency(order?.total_amount);
  const internalOrdersTotal = parseCurrency(order?.internal_orders_total || 0);
  const orderTotal = orderSiigoTotal + internalOrdersTotal;

  // Direct calculation: pending = total - paid
  const paidAmount = parseCurrency(order?.paid_amount || 0);
  const pendingAmount = orderTotal - paidAmount;
  const requiresPayment = pendingAmount > 0;

  // Soportar multi-rol: considerar base role y roles adicionales en user.roles
  const normalizeRole = (s = '') => String(s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  const baseRole = normalizeRole(user?.role);
  const extraRoles = Array.isArray(user?.roles)
    ? user.roles.map(r => normalizeRole(r.role_name || r))
    : [];
  const hasRole = (r) => baseRole === normalizeRole(r) || extraRoles.includes(normalizeRole(r));

  const isBillingValidator = hasRole('facturador') || hasRole('facturacion') || currentView === 'facturacion';
  // Cartera/Admin son validadores de banco, pero si están en la vista de facturación o tienen ese rol, prioritizamos el flujo de registro.
  const isWalletValidator = (hasRole('cartera') || hasRole('admin')) && currentView !== 'facturacion' && !hasRole('facturador') && !hasRole('facturacion');
  const isCarteraRole = hasRole('cartera');

  // Detección simple del canal desde texto (para autocompletar sin mostrar campos)
  const detectProviderFromStringLocal = (text = '') => {
    const t = String(text).toLowerCase();
    if (t.includes('nequi')) return 'nequi';
    if (t.includes('daviplata')) return 'daviplata';
    if (t.includes('bancolombia') || t.includes('banco')) return 'bancolombia';
    if (t.includes('mercadopago')) return 'mercadopago';
    if (t.includes('bold')) return 'bold';
    return 'otro';
  };

  // Autocompletar (oculto) proveedor/referencia/fecha para mensajero, no relaja validaciones de cartera
  useEffect(() => {
    if (!isWalletValidator) {
      const pm = normalizePaymentMethod(order?.payment_method);
      if (pm === 'transferencia' || pm === 'pago_electronico') {
        const fromOrder = (order?.electronic_payment_type || order?.payment_provider || '').toLowerCase();
        const detected = detectProviderFromStringLocal(order?.notes || '');
        const bank = (fromOrder || detected || 'otro');
        const ref = `auto-${Date.now()}`;
        const dateStr = new Date().toISOString().slice(0, 10);
        setFormData(prev => ({
          ...prev,
          bankName: prev.bankName || bank,
          paymentReference: prev.paymentReference || ref,
          paymentDate: prev.paymentDate || dateStr
        }));
      }
    }
  }, [user, order]);
  const [customerCredit, setCustomerCredit] = useState(null);
  const [loadingCredit, setLoadingCredit] = useState(false);

  // FUNCIONES DE TRADUCCIÓN UNIVERSALES
  const getPaymentMethodLabel = (method) => {
    const v = (method || '').toString().toLowerCase();
    const labels = {
      'efectivo': 'Efectivo',
      'efectivo_ruta_domicilio': 'Efectivo Ruta Domicilio',
      'transferencia': 'Transferencia',
      'tarjeta_credito': 'Tarjeta de Crédito',
      'cliente_credito': 'Cliente a Crédito',
      'credito': 'Cliente a Crédito',
      'pago_electronico': 'Pago Electrónico',
      'contraentrega': 'Contraentrega'
    };
    return labels[v] || 'No Especificado';
  };

  // Normaliza el método de pago a un token canónico sin acentos ni espacios
  const normalizePaymentMethod = (method) => {
    const raw = (method || '').toString().trim().toLowerCase();
    // Quitar acentos
    const noAccents = raw.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    // Reemplazar espacios por guion bajo
    const token = noAccents.replace(/\s+/g, '_');
    // Aliases comunes
    if (token.includes('electronico')) return 'pago_electronico';
    if (token === 'tarjeta_credito' || token === 'tarjeta') return 'tarjeta_credito';
    return token;
  };

  const getDeliveryMethodLabel = (method) => {
    const v = (method || '').toString().toLowerCase();
    const labels = {
      'domicilio': 'Domicilio',
      'domicilio_ciudad': 'Domicilio Ciudad',
      'domicilio_local': 'Domicilio Local',
      'mensajeria_urbana': 'Mensajería Urbana',
      'recogida_tienda': 'Recoge en Bodega',
      'recoge_bodega': 'Recoge en Bodega',
      'envio_nacional': 'Envío Nacional',
      'nacional': 'Envío Nacional'
    };
    return labels[v] || 'No Especificado';
  };

  const getShippingDateLabel = (order) => {
    const v = order?.shipping_date;
    if (v) {
      let d;
      if (v instanceof Date) {
        d = v;
      } else if (typeof v === 'string') {
        // Soportar formatos: 'YYYY-MM-DD', 'YYYY-MM-DD HH:mm:ss', ISO, etc.
        if (/^\d{4}-\d{2}-\d{2}$/.test(v)) {
          const [y, m, dd] = v.split('-').map(Number);
          d = new Date(y, m - 1, dd);
        } else {
          d = new Date(v.replace(' ', 'T'));
        }
      }
      if (d && !isNaN(d.getTime())) {
        return d.toLocaleDateString('es-CO');
      }
      // Si llega un formato raro, mostrar texto crudo para no confundir
      return String(v);
    }
    const dm = (order?.delivery_method || '').toLowerCase();
    if (dm === 'recogida_tienda' || dm === 'recoge_bodega') {
      return 'No aplica (recoge en bodega)';
    }
    return 'No especificada';
  };

  const formatAmount = (amount) => {
    const numAmount = parseFloat(amount || 0);
    return numAmount.toLocaleString('es-CO');
  };

  // Formatear notas de SIIGO a múltiples líneas legibles
  const formatSiigoNotes = (raw = '') => {
    if (!raw) return '';
    let t = String(raw).replace(/\s+/g, ' ').trim();
    const labels = [
      'OBSERVACIONES:',
      'ESTADO DE PAGO:',
      'MEDIO DE PAGO:',
      'FORMA DE PAGO DE ENVIO:',
      'FORMA DE PAGO DE ENVÍO:',
      'NOMBRE:',
      'NIT:',
      'TELÉFONO:',
      'TELEFONO:',
      'DEPARTAMENTO:',
      'CIUDAD:',
      'DIRECCIÓN:',
      'DIRECCION:',
      'NOTA:',
      'NOTAS:'
    ];
    // Insertar salto de línea antes de cada etiqueta conocida
    labels.forEach(lbl => {
      const esc = lbl.replace(/([.*+?^${}()|\[\]\\])/g, '\\$1');
      const re = new RegExp(`\\s*${esc}`, 'g');
      t = t.replace(re, `\n${lbl} `);
    });
    // Limpiar saltos duplicados y espacios
    t = t.replace(/\n+/g, '\n').trim();
    return t;
  };

  // Margen permitido para diferencias de redondeo en validación de montos
  // Regla: máximo entre $200 y 0.5% del total, con tope de $5.000
  const getAllowedTolerance = (orderTotal) => {
    const total = parseFloat(orderTotal || 0);
    const minTol = 200; // $200
    const relTol = total * 0.005; // 0.5%
    const tol = Math.max(minTol, relTol);
    return Math.min(tol, 5000);
  };

  const initialFormState = {
    // Para transferencias y efectivo
    paymentProofImage: null,
    paymentAmount: 0,
    paymentDate: (() => {
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    })(),
    bankName: '',
    creditApproved: false,
    validationNotes: '',
    validationType: 'approved',
    responsibilityChecked: false,
    cashType: 'efectivo' // Tipo de efectivo: 'efectivo' o 'efectivo_ruta_domicilio'
  };

  const [formData, setFormData] = useState(initialFormState);

  // Reset form data when modal opens or order changes
  useEffect(() => {
    if (isOpen && order?.id) {
      setFormData(prev => ({
        ...initialFormState,
        // Mantener fecha actual por defecto si es necesario, o recalcular
        paymentDate: initialFormState.paymentDate
      }));
    }
  }, [isOpen, order?.id]);



  // Cargar información de crédito del cliente si el método de pago es cliente_credito
  useEffect(() => {
    const pm = (order?.payment_method || '').toLowerCase();
    if ((pm === 'cliente_credito' || pm === 'credito') && order?.customer_name) {
      loadCustomerCredit();
    }
  }, [order]);

  // Prellenar proveedor/canal desde facturación para Pago Electrónico
  useEffect(() => {
    const pmNorm = normalizePaymentMethod(order?.payment_method);
    if (pmNorm === 'pago_electronico') {
      const pmLabel = String(order?.payment_method || '').toLowerCase();
      let provider = (order?.electronic_payment_type || order?.payment_provider || '').toLowerCase();

      if (!provider) {
        if (pmLabel.includes('bold')) provider = 'bold';
        if (pmLabel.includes('mercado')) provider = 'mercadopago';
      }

      if (provider) {
        setFormData(prev => ({ ...prev, bankName: provider }));
      }
    }
  }, [order]);

  useEffect(() => {
    if (normalizePaymentMethod(order?.payment_method) === 'pago_electronico') {
      setFormData(prev => ({
        ...prev,
        // Prellenar monto para pago electrónico (generalmente es el total)
        paymentAmount: (prev.paymentAmount === '' || prev.paymentAmount === 0) ? order.total_amount : prev.paymentAmount
      }));
    }
  }, [order]);

  // Actualizar paymentAmount y bankName cuando linkedMovements cambia
  useEffect(() => {
    if (linkedMovements.length > 0) {
      const totalAmount = linkedMovements.reduce((sum, m) => sum + m.valor, 0);
      setFormData(prev => ({
        ...prev,
        paymentAmount: totalAmount,
        bankName: 'bancolombia'  // Auto-completar banco de origen
      }));
    }
  }, [linkedMovements]);

  // Auto-completar banco cuando se cargan pagos ya vinculados históricamente
  useEffect(() => {
    if (alreadyLinked.length > 0) {
      const totalAmount = alreadyLinked.reduce((sum, p) => sum + (parseFloat(p.reconciled_amount || p.valor) || 0), 0);
      setFormData(prev => ({
        ...prev,
        paymentAmount: totalAmount,
        bankName: 'bancolombia'  // Auto-completar banco de origen
      }));
    }
  }, [alreadyLinked]);


  const loadCustomerCredit = async () => {
    // Limpiar estado previo para evitar mostrar datos del cliente anterior
    setCustomerCredit(null);
    setLoadingCredit(true);
    try {
      // Preferir identificación/NIT para el cruce (más confiable que el nombre)
      const rawNit = (order?.customer_identification || order?.customer_nit || '').toString();
      const nit = rawNit.replace(/[^0-9]/g, '');
      const base = `/api/wallet/customer-credit/${encodeURIComponent(order.customer_name || '')}`;
      const url = nit ? `${base}?nit=${encodeURIComponent(nit)}` : base;

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setCustomerCredit(data.data || null);
      } else {
        console.error('Error cargando información de crédito');
        setCustomerCredit(null);
      }
    } catch (error) {
      console.error('Error:', error);
      setCustomerCredit(null);
    } finally {
      setLoadingCredit(false);
    }
  };

  // Normaliza inputs numéricos (acepta 24.600, 24,600, etc.)
  const normalizeNumericInput = (v) => {
    if (v === '' || v === null || v === undefined) return '';
    const cleaned = String(v).replace(/[^0-9.-]/g, '');
    const num = parseFloat(cleaned);
    return Number.isFinite(num) ? num : '';
  };

  const handleInputChange = (field, value) => {
    const numericFields = ['paymentAmount', 'transferredAmount', 'cashAmount'];
    const newValue = numericFields.includes(field) ? normalizeNumericInput(value) : value;
    setFormData(prev => {
      const next = { ...prev, [field]: newValue };
      return next;
    });
  };

  // Mantenemos la compatibilidad: selector simple de archivo para pago principal
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validar tipo de archivo
      if (!file.type.startsWith('image/')) {
        toast.error('Solo se permiten archivos de imagen');
        return;
      }

      // Validar tamaño (máximo 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('El archivo no puede ser mayor a 5MB');
        return;
      }

      setFormData(prev => ({
        ...prev,
        paymentProofImage: file
      }));
    }
  };


  const [showOverpaymentConfirmation, setShowOverpaymentConfirmation] = useState(false);
  const [overpaymentDetails, setOverpaymentDetails] = useState(null);
  const [pendingValidationType, setPendingValidationType] = useState('approved');

  const confirmOverpayment = () => {
    setShowOverpaymentConfirmation(false);
    // Re-trigger validation with force flag
    handleValidate(pendingValidationType, true);
  };

  const validateForm = (forceOverpayment = false) => {
    const pm = normalizePaymentMethod(order?.payment_method);

    // Si no hay cobro pendiente, omitir validaciones de transferencia SOLO para roles distintos a cartera
    if (pm === 'transferencia' && !requiresPayment && !isWalletValidator) {
      return true;
    }

    if (pm === 'transferencia') {
      // VALIDACIÓN ULTRA-ESTRICTA DE MONTO
      const orderSiigoTotal = parseFloat(order.total_amount || 0);
      const internalTotal = parseFloat(order.internal_orders_total || 0);
      const orderTotal = orderSiigoTotal + internalTotal;

      const amount = linkedMovements.length > 0
        ? linkedMovements.reduce((sum, m) => sum + Number(m.valor), 0)
        : parseFloat(formData.paymentAmount || 0);

      // Validación de campo obligatorio
      if (linkedMovements.length === 0 && (!formData.paymentAmount || formData.paymentAmount === '')) {
        toast.error('❌ CAMPO OBLIGATORIO: Debe ingresar el monto transferido');
        return false;
      }

      // Validación de monto válido (no negativos, no cero)
      if (amount <= 0) {
        toast.error('❌ MONTO INVÁLIDO: El monto transferido debe ser mayor a cero');
        return false;
      }

      // Validación de monto con tolerancia (redondeos)
      const diff = Math.abs(amount - orderTotal);
      if (diff > getAllowedTolerance(orderTotal)) {
        const difference = orderTotal - amount;
        if (difference > 0) {
          // Pago Parcial: Permitir, pero notificar.
          // El backend y el frontend ya manejan esto como saldo pendiente.
          toast('⚠️ Transferencia parcial detectada. Se cobrará el restante al momento de la entrega.', {
            icon: '💰',
            duration: 4000
          });
          // NO retornamos false, dejamos pasar.
        } else {
          // Pago Excesivo (Sobrepago)
          if (!forceOverpayment) {
            setOverpaymentDetails({
              amount,
              orderTotal,
              excess: Math.abs(difference)
            });
            setShowOverpaymentConfirmation(true);
            return false;
          }
          // If forced, continue
        }
      }

      // Validaciones adicionales obligatorias

      // Checkbox de responsabilidad OBLIGATORIO
      if (!formData.responsibilityChecked) {
        toast.error('❌ DEBE ACEPTAR RESPONSABILIDAD: Marque la casilla confirmando que verificó el dinero en el banco.');
        return false;
      }

      if (isWalletValidator && !formData.bankName.trim()) {
        toast.error('❌ BANCO FALTANTE: Debe seleccionar el banco de origen');
        return false;
      }
    }

    // Pago electrónico (Bold/MercadoPago): comprobante y proveedor obligatorios
    if (pm === 'pago_electronico') {
      const hasEvidence = (existingEvidences.length > 0 || sessionUploads > 0 || formData.paymentProofImage);
      if (!hasEvidence) {
        toast.error('❌ COMPROBANTE FALTANTE: Debe subir el comprobante del pago electrónico');
        return false;
      }
      if (isWalletValidator) {
        const provider = (formData.bankName || '').toLowerCase();
        const pMatch = provider.toLowerCase();
        if (!['bold', 'mercadopago', 'mercado_pago', 'mercado pago'].includes(pMatch)) {
          toast.error('❌ PROVEEDOR REQUERIDO: Debe seleccionar Bold o Mercado Pago');
          return false;
        }
      }
    }

    if (pm === 'efectivo') {
      const hasEvidence = (existingEvidences.length > 0 || sessionUploads > 0 || formData.paymentProofImage);
      if (!hasEvidence && !isWalletValidator) {
        toast.error('Debe subir la imagen del pago en efectivo');
        return false;
      }
      // Checkbox de responsabilidad OBLIGATORIO para Efectivo también (si es validador)
      if (isWalletValidator && !formData.responsibilityChecked) {
        toast.error('❌ DEBE ACEPTAR RESPONSABILIDAD: Marque la casilla confirmando que verificó el dinero en el banco.');
        return false;
      }
    }

    if (pm === 'cliente_credito' || pm === 'credito') {
      // Política: si el cliente no tiene cupo asignado o no alcanza, Cartera decide.
      // No bloqueamos la aprobación; solo mostramos advertencias informativas.
      if (!customerCredit) {
        toast('Validación de crédito sin información de cartera: se permitirá continuar bajo criterio.');
        return true;
      }

      if ((customerCredit.status || '').toLowerCase() !== 'active') {
        toast('Cliente con crédito no activo. Se permitirá continuar bajo criterio.');
        return true;
      }

      const orderAmount = parseFloat(order.total_amount || 0);
      const availableCredit = parseFloat(customerCredit.available_credit || 0);

      if (orderAmount > availableCredit) {
        toast('Pedido excede cupo disponible. Se permitirá continuar bajo criterio.', { duration: 6000 });
        return true;
      }
    }

    return true;
  };

  const handleValidate = async (validationType = 'approved', forceOverpayment = false) => {
    if (validationType === 'approved' && filesSelectedCount > 0 && existingEvidences.length === 0) {
      toast.error('Tiene archivos seleccionados sin subir. Por favor haga clic en "Subir X archivo(s)" antes de validar.');
      return;
    }
    // Para rechazos, solo validamos que haya notas
    if (validationType === 'rejected') {
      if (!formData.validationNotes.trim()) {
        toast.error('Debe especificar el motivo por el cual no puede pasar a logística');
        return;
      }
    } else {
      // Para aprobaciones, validamos el formulario completo
      // Guardamos el tipo por si sale el modal
      setPendingValidationType(validationType);
      if (!validateForm(forceOverpayment)) return;

      // ✅ VALIDACIÓN UNIVERSAL ANTI-DUPLICADOS (para todos los métodos de pago)
      // Prevenir duplicados cuando el pedido regresa a Cartera
      if (alreadyLinked && alreadyLinked.length > 0) {
        const pm = normalizePaymentMethod(order.payment_method);

        // Solo aplicar para pagos que se registran manualmente (no crédito puro)
        if (['efectivo', 'transferencia', 'pago_electronico'].includes(pm)) {
          const proposedAmount = linkedMovements.length > 0
            ? linkedMovements.reduce((sum, m) => sum + Number(m.valor), 0)
            : parseFloat(formData.paymentAmount || 0);

          const proposedDate = formData.paymentDate || new Date().toISOString().slice(0, 10);

          // Calcular total ya pagado en histórico
          const totalPaidInHistory = alreadyLinked.reduce((sum, p) => {
            return sum + parseFloat(p.reconciled_amount || p.valor || 0);
          }, 0);

          // ⚠️ VALIDACIÓN 1: Mostrar WARNING si detecta monto/fecha similar
          // NO bloquea - solo alerta al usuario
          const hasSimilarPayment = alreadyLinked.some(payment => {
            const paidAmount = parseFloat(payment.reconciled_amount || payment.valor || 0);
            const paidDate = (payment.reconciled_at || payment.fecha || '').slice(0, 10);

            const amountMatch = Math.abs(proposedAmount - paidAmount) < 100;
            const dateMatch = proposedDate === paidDate;

            return amountMatch && dateMatch;
          });

          if (hasSimilarPayment) {
            // ⚠️ WARNING - NO bloquea, solo alerta
            toast(
              `⚠️ ATENCIÓN: Ya existe un pago de ${formatAmount(proposedAmount)} registrado el ${proposedDate}. Verifica que no sea duplicado antes de continuar.`,
              {
                duration: 8000,
                icon: '⚠️',
                style: {
                  background: '#FEF3C7',
                  color: '#92400E',
                  border: '2px solid #F59E0B',
                  fontWeight: 'bold'
                }
              }
            );
            // ✅ NO retornar - permitir que el usuario continúe si está seguro
          }

          // ❌ VALIDACIÓN 2: PERMITIR si pedido está pagado PERO falta RC
          // Detectar si el pedido ya está 100% pagado
          const isFullyPaid = totalPaidInHistory >= orderTotal;
          const missingRC = !order.siigo_payment_info || order.siigo_payment_info === '[]' || order.siigo_payment_info === '';

          // Logging para debug
          console.log('[RC-FIX DEBUG]', {
            isFullyPaid,
            missingRC,
            proposedAmount,
            totalPaidInHistory,
            orderTotal,
            paymentAmountRaw: formData.paymentAmount,
            linkedMovementsCount: linkedMovements.length
          });

          // ✅ CASO 1: Pedido pagado + falta RC → PERMITIR (PRIORIDAD MÁXIMA)
          // Si el pedido ya está pagado pero falta el RC, SIEMPRE permitir
          // Esto permite crear el RC sin duplicar el pago
          if (isFullyPaid && missingRC) {
            console.log('✅ [RC-FIX] PERMITIENDO: Pedido completamente pagado pero sin RC. Solo creará RC.');
            toast(
              `💡 Este pedido ya está pagado ($${formatAmount(totalPaidInHistory)}). Solo se creará el RC faltante en Siigo.`,
              {
                duration: 6000,
                icon: '📋',
                style: {
                  background: '#DBEAFE',
                  color: '#1E40AF',
                  border: '2px solid #3B82F6'
                }
              }
            );
            // ✅ CONTINUAR - solo creará RC, no registrará pago
          } else if (isFullyPaid && proposedAmount > 0) {
            // ❌ CASO 2: Pedido pagado + YA TIENE RC + usuario intenta registrar OTRO pago → BLOQUEAR
            console.log('[RC-FIX] BLOQUEANDO: Pedido pagado + ya tiene RC + monto > 0');
            toast.error(
              `🚫 Este pedido ya está completamente pagado ($${formatAmount(totalPaidInHistory)} de $${formatAmount(orderTotal)}). No se pueden registrar más pagos.`,
              {
                duration: 7000,
                icon: '💰',
                style: {
                  background: '#FEE2E2',
                  color: '#991B1B',
                  border: '2px solid #DC2626',
                  fontWeight: 'bold'
                }
              }
            );
            setLoading(false);
            return; // ❌ BLOQUEAR SOBREPAGO
          }


          // ✅ Si el pago nuevo + histórico no excede el total, permitir
          // Esto permite pagos parciales legítimos del mismo monto
        }
      }
    }

    setLoading(true);
    try {
      const formDataToSend = new FormData();

      const pm = normalizePaymentMethod(order.payment_method);

      // Determinar método de pago final: si es efectivo y hay cashType seleccionado, usar ese
      const finalPaymentMethod = (pm === 'efectivo' && formData.cashType)
        ? formData.cashType
        : order.payment_method;

      // Datos básicos
      formDataToSend.append('orderId', order.id);
      formDataToSend.append('paymentMethod', finalPaymentMethod);
      formDataToSend.append('validationType', validationType);
      if (isBillingValidator) {
        formDataToSend.append('isRegistrationOnly', 'true');
      }
      // Inyectar nota técnica si Cartera marca que el efectivo lo cobra el mensajero
      const notesWithFlag = formData.cashByMessenger
        ? `${formData.validationNotes || ''} [cash_pending_by_messenger]`
        : formData.validationNotes;
      formDataToSend.append('validationNotes', notesWithFlag);

      // Solo agregar datos de pago si es aprobación
      if (validationType === 'approved') {
        // Para transferencias
        if (pm === 'transferencia') {
          // Pago simple o vinculado
          const finalPaymentAmount = linkedMovements.length > 0
            ? linkedMovements.reduce((sum, m) => sum + Number(m.valor), 0)
            : formData.paymentAmount;
          formDataToSend.append('paymentAmount', finalPaymentAmount);


          if (formData.paymentProofImage) {
            formDataToSend.append('paymentProofImage', formData.paymentProofImage);
          }

          // Enviar IDs de movimientos vinculados (pueden ser múltiples)
          if (linkedMovements.length > 0) {
            const movementIds = linkedMovements.map(m => m.id).join(',');
            formDataToSend.append('linkedMovementIds', movementIds);
          }

          formDataToSend.append('paymentDate', formData.paymentDate);
          formDataToSend.append('bankName', formData.bankName);
        }

        // Para pago electrónico (Bold/MercadoPago/Mercado Pago)
        if (pm === 'pago_electronico' || pm === 'mercado_pago') {
          if (formData.paymentProofImage) {
            formDataToSend.append('paymentProofImage', formData.paymentProofImage);
          }
          // Enviar fecha y proveedor (bankName usado como canal)
          formDataToSend.append('paymentDate', formData.paymentDate);
          formDataToSend.append('bankName', formData.bankName);
          // Registrar monto (total del pedido)
          formDataToSend.append('paymentAmount', formData.paymentAmount || order.total_amount);
        }

        // Para efectivo
        if (pm === 'efectivo') {
          if (formData.paymentProofImage) {
            formDataToSend.append('paymentProofImage', formData.paymentProofImage);
          }
          formDataToSend.append('paymentAmount', formData.paymentAmount || order.total_amount);
          formDataToSend.append('paymentDate', formData.paymentDate);
        }

        // Para crédito
        if (pm === 'cliente_credito' || pm === 'credito') {
          formDataToSend.append('creditApproved', formData.creditApproved);
          formDataToSend.append('customerCreditLimit', customerCredit?.credit_limit || 0);
          formDataToSend.append('customerCurrentBalance', customerCredit?.current_balance || 0);
        }
      }
      const result = await onValidate(formDataToSend);
      onClose();

      if (validationType === 'approved') {
        if (result?.siigo_voucher?.reused) {
          // RC Reutilizado - mostrar amarillo
          const rcName = result.siigo_voucher.name || result.siigo_voucher.message || 'Existente';
          toast(`Pago Validado - RC Vinculado: ${rcName}`, {
            duration: 6000,
            icon: '♻️',
            style: {
              background: '#FEF3C7',
              color: '#92400E',
              border: '1px solid #F59E0B'
            }
          });
        } else if (result?.siigo_voucher?.name) {
          // RC Nuevo - mostrar verde
          toast.success(`Pago Validado y RC Creado: ${result.siigo_voucher.name}`, { duration: 6000, icon: '✅' });
        } else if (order.is_service) {
          toast.success('Pago validado exitosamente');
        } else {
          toast.success(result.message || 'Pago validado exitosamente');
        }
      } else {
        toast.success('Pedido marcado como no apto para logística');
      }
    } catch (error) {
      console.error('Error validando pago:', error);
      toast.error('Error al procesar la validación');
    } finally {
      setLoading(false);
    }
  };

  // Handler para devolución exitosa a Facturación
  const handleReturnToBillingSuccess = () => {
    // Cerrar modal actual
    onClose();
    // Refrescar la lista de órdenes
    if (onValidate) {
      onValidate();
    }
  };


  const getValidationType = () => {
    switch ((order?.payment_method || '').toLowerCase()) {
      case 'transferencia':
        return 'Validación de Transferencia';
      case 'efectivo':
        return 'Validación de Pago en Efectivo';
      case 'cliente_credito':
      case 'credito':
        return 'Validación de Cupo de Crédito';
      default:
        return 'Validación de Pago';
    }
  };

  const canApproveCredit = () => {
    if (!customerCredit || !['cliente_credito', 'credito'].includes((order?.payment_method || '').toLowerCase())) return false;
    return (customerCredit.status === 'active') &&
      (orderTotal <= parseFloat(customerCredit.available_credit || 0));
  };

  const getCreditStatus = () => {
    if (!customerCredit || !['cliente_credito', 'credito'].includes((order?.payment_method || '').toLowerCase())) return null;
    const availableCredit = customerCredit.available_credit;
    return {
      exceedsCredit: orderTotal > availableCredit,
      orderAmount: orderTotal,
      availableCredit
    };
  };

  const [filesSelectedCount, setFilesSelectedCount] = useState(0);

  const pmNorm = normalizePaymentMethod(order?.payment_method);
  const isElectronic = pmNorm === 'pago_electronico' || pmNorm === 'mercado_pago';
  const approveDisabled = loading || validationBlocked || !cashSessionOpen || (
    isElectronic && isWalletValidator && (
      !['bold', 'mercadopago', 'mercado_pago', 'mercado pago'].includes((formData.bankName || '').toLowerCase())
    )
  );

  // No bloqueamos por paymentProofImage en formData, sino por sessionUploads en validateForm
  // Variables already defined above: orderSiigoTotal, internalOrdersTotal, orderTotal

  const allowedTolerance = getAllowedTolerance(orderTotal);
  const singlePaymentWithinTolerance = Math.abs(parseFloat(formData.paymentAmount || 0) - orderTotal) <= allowedTolerance;



  const deliveryMethod = (order?.delivery_method || '').toLowerCase();
  const isPickup = ['recogida_tienda', 'recoge_bodega'].includes(deliveryMethod);
  const isDomicile = !isPickup;

  if (!isOpen || !order) return null;

  return (
    <div
      ref={modalRef}
      tabIndex={0}
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-7xl w-full mx-4 max-h-[95vh] flex flex-col">
        {/* Catcher oculto para activar onPaste sin clic previo */}
        <div
          ref={pasteCatcherRef}
          contentEditable
          tabIndex={-1}
          aria-hidden="true"
          style={{ position: 'absolute', width: 1, height: 1, opacity: 0, pointerEvents: 'none' }}
        />

        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gray-50 rounded-t-lg">
          <div>
            <h2 className="text-xl font-bold text-gray-800">
              {getValidationType()}
            </h2>
            <div className="flex items-center flex-wrap gap-4 mt-2">
              <div className="flex items-baseline gap-2">
                <span className="text-xs text-gray-500 uppercase font-bold tracking-wider">Pedido</span>
                <span className="text-4xl font-black text-blue-900 tracking-tight">{order.order_number}</span>
              </div>
              <div className="text-2xl font-bold text-gray-800 border-l-2 border-gray-300 pl-4 py-1">
                {order.customer_name}
              </div>
              {order.siigo_public_url && (
                <a
                  href={order.siigo_public_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-700 hover:bg-blue-100 rounded-lg text-sm font-medium transition-colors border border-blue-200 ml-2"
                  title="Ver Factura SIIGO"
                >
                  <Icons.FileText className="w-4 h-4" />
                  Ver Factura
                </a>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors bg-white rounded-full p-1 hover:bg-gray-100"
          >
            <Icons.X className="w-6 h-6" />
          </button>
        </div>

        {/* Content Scrollable */}
        <div className="p-6 overflow-y-auto custom-scrollbar flex-1 bg-gray-100">



          {/* MAIN SPLIT VIEW LAYOUT */}
          <div className="flex flex-col lg:flex-row gap-6 h-full min-h-[500px]">

            <div className="w-full lg:w-1/2 flex flex-col gap-4">
              <div
                className="w-full bg-gray-100 rounded-lg overflow-hidden relative flex items-center justify-center h-[65vh] border border-gray-300 shadow-inner"
              >
                {previewImage ? (
                  imageLoadError ? (
                    <div className="flex flex-col items-center justify-center p-8 bg-gray-100 text-red-600 h-full w-full text-center">
                      <Icons.AlertTriangle className="w-12 h-12 mb-4" />
                      <p className="font-bold text-lg mb-2">Error cargando imagen</p>
                      <p className="font-mono text-xs break-all bg-white p-2 border border-gray-200 rounded max-w-full text-red-500 mb-2">
                        {imageLoadError}
                      </p>
                      <button onClick={() => setPreviewImage(null)} className="mt-4 text-xs underline text-gray-500 hover:text-gray-800">Volver</button>
                    </div>
                  ) : (
                    <img
                      src={
                        (typeof previewImage === 'string'
                          ? (previewImage.startsWith('http') ? previewImage : `${process.env.REACT_APP_API_URL || '/api'}${previewImage.startsWith('/') ? '' : '/'}${previewImage}`)
                          : URL.createObjectURL(previewImage))
                      }
                      alt="Payment Proof Preview"
                      className="object-contain"
                      style={{ height: '100%', width: 'auto', maxWidth: '100%', objectFit: 'contain' }}
                      onError={(e) => {
                        const currentSrc = e.target.src;
                        if (typeof previewImage === 'string' && !currentSrc.includes('/api/public/evidence')) {
                          const filename = previewImage.split('/').pop();
                          const newSrc = `/api/public/evidence/${filename}/content`;
                          e.target.src = newSrc;
                        } else {
                          setImageLoadError(currentSrc);
                        }
                      }}
                    />
                  )
                ) : (
                  <div className="text-gray-500 flex flex-col items-center p-8 text-center">
                    <Icons.Image className="w-16 h-16 mb-4 opacity-20" />
                    <p className="text-sm font-medium">Sin comprobante seleccionado</p>
                    <p className="text-xs opacity-60 mt-1">Selecciona una miniatura o sube una imagen</p>
                  </div>
                )}
              </div>

              {/* Preview Modal (Full Screen Zoom) */}
              {isZoomed && previewImage && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-90 p-4 backdrop-blur-sm" onClick={(e) => { e.stopPropagation(); setIsZoomed(false); }}>
                  <img
                    src={
                      (typeof previewImage === 'string'
                        ? (previewImage.startsWith('http') ? previewImage : `${process.env.REACT_APP_API_URL || '/api'}${previewImage.startsWith('/') ? '' : '/'}${previewImage}`)
                        : URL.createObjectURL(previewImage))
                    }
                    alt="Preview Full"
                    className="max-w-full max-h-[95vh] object-contain rounded-lg shadow-2xl"
                    onClick={(e) => e.stopPropagation()} // Prevent closing when clicking image
                  />
                  <button
                    onClick={() => setIsZoomed(false)}
                    className="absolute top-4 right-4 bg-white/10 hover:bg-white/20 text-white rounded-full p-2 transition-colors cursor-pointer"
                  >
                    <Icons.X className="w-8 h-8" />
                  </button>
                </div>
              )}

              {/* Thumbnails Row */}
              {(existingEvidences.length > 0 || (order.files && order.files.length > 0)) && (
                <div className="bg-white p-3 rounded-lg border border-gray-200 shadow-sm">
                  <p className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wide">Comprobantes Disponibles</p>
                  <div className="flex gap-2 overflow-x-auto pb-1 custom-scrollbar">
                    {/* Existing Evidences */}
                    {existingEvidences.map((url, idx) => (
                      <button
                        key={`exist-${idx}`}
                        type="button"
                        onClick={() => setPreviewImage(url)}
                        className={`relative w-16 h-16 flex-shrink-0 rounded-md overflow-hidden border-2 transition-all ${previewImage === url ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-200 hover:border-gray-300'
                          }`}
                      >
                        <img
                          src={url.startsWith('http') ? url : `${process.env.REACT_APP_API_URL || '/api'}${url.startsWith('/') ? '' : '/'}${url}`}
                          alt={`Evidence ${idx}`}
                          className="w-full h-full object-cover"
                        />
                      </button>
                    ))}
                    {/* Order Files (Legacy) */}
                    {order.files && order.files.map((file, idx) => (
                      <button
                        key={`file-${idx}`}
                        type="button"
                        onClick={() => setPreviewImage(file)}
                        className={`relative w-16 h-16 flex-shrink-0 rounded-md overflow-hidden border-2 transition-all ${previewImage === file ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-200 hover:border-gray-300'
                          }`}
                      >
                        <img
                          src={file.startsWith('http') ? file : `${process.env.REACT_APP_API_URL || '/api'}/..${file.startsWith('/') ? '' : '/'}${file}`}
                          alt={`File ${idx}`}
                          className="w-full h-full object-cover"
                        />
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-200 p-5 shadow-sm">
                <h3 className="font-bold text-blue-900 mb-4 flex items-center text-sm uppercase tracking-wide border-b border-blue-200 pb-2">
                  <Icons.Calculator className="w-4 h-4 mr-2" />
                  Resumen Financiero
                </h3>
                <div className="space-y-3">
                  {/* Subtotal Siigo */}
                  <div className="flex justify-between items-center text-sm group">
                    <span className="font-medium text-blue-700 group-hover:text-blue-900 transition-colors">Subtotal Siigo</span>
                    <span className="font-bold text-blue-800 bg-white px-2 py-0.5 rounded border border-blue-100 shadow-sm">
                      ${formatAmount(orderSiigoTotal)}
                    </span>
                  </div>

                  {/* Internal Orders */}
                  {internalOrdersTotal > 0 && (
                    <div className="flex justify-between items-center text-sm group animate-in slide-in-from-left-2 fade-in duration-300">
                      <div className="flex items-center">
                        <span className="font-medium text-indigo-700 group-hover:text-indigo-900 transition-colors">Especiales</span>
                        <span className="ml-2 text-[10px] bg-indigo-100 text-indigo-600 px-1.5 py-0.5 rounded-full font-bold">INTERNO</span>
                      </div>
                      <span className="font-bold text-indigo-800 bg-white px-2 py-0.5 rounded border border-indigo-100 shadow-sm">
                        + ${formatAmount(internalOrdersTotal)}
                      </span>
                    </div>
                  )}

                  {/* Divider */}
                  <div className="border-t border-blue-200/60 my-2"></div>

                  {/* Total */}
                  <div className="flex justify-between items-end">
                    <span className="text-blue-900 font-bold text-sm mb-1">TOTAL A PAGAR</span>
                    <span className="text-blue-900 font-black text-2xl tracking-tight bg-blue-100/50 px-3 py-1 rounded-lg border border-blue-200 shadow-sm">
                      ${formatAmount(orderTotal)}
                    </span>
                  </div>
                </div>
              </div>

            </div>

            {/* RIGHT COLUMN: Validation Form (50%) */}
            <div className="w-full lg:w-1/2 flex flex-col gap-6">


              {/* Pagos YA vinculados (Histórico de Conciliaciones) */}
              {alreadyLinked && alreadyLinked.length > 0 && (
                <div className="bg-teal-50 rounded-xl shadow-sm border border-teal-200 p-4 animate-fade-in relative overflow-hidden">
                  <div className="absolute top-0 right-0 p-2 opacity-10">
                    <Icons.Link className="w-24 h-24 text-teal-600" />
                  </div>
                  <h3 className="font-bold text-teal-900 mb-3 flex items-center relative z-10">
                    <Icons.CheckCircle className="w-5 h-5 mr-2" />
                    📋 Pagos Ya Registrados ({alreadyLinked.length}) - Solo Información
                  </h3>
                  <div className="space-y-2 relative z-10 max-h-48 overflow-y-auto custom-scrollbar pr-1">
                    {alreadyLinked.map((payment, idx) => (
                      <div key={idx} className="bg-white/80 backdrop-blur-sm p-3 rounded-lg border border-teal-100 hover:border-teal-300 transition-colors shadow-sm">
                        <div className="flex justify-between items-start">
                          <div>
                            <div className="font-bold text-teal-800 text-base">
                              ${formatAmount(payment.reconciled_amount || payment.valor)}
                            </div>
                            <div className="text-xs text-gray-600 mt-0.5 font-medium">
                              {payment.descripcion}
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-[10px] font-bold text-teal-600 bg-teal-100 px-1.5 py-0.5 rounded uppercase tracking-wider">
                              ✓ Ya Conciliado
                            </div>
                            <div className="text-[10px] text-gray-500 mt-1">
                              {formatDate(payment.reconciled_at || payment.fecha)}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 pt-2 border-t border-teal-200 flex justify-between items-center text-xs text-teal-700 font-medium bg-teal-100/50 px-2 py-1.5 rounded">
                    <span className="flex items-center">
                      <Icons.Info className="w-4 h-4 mr-1.5" />
                      Total Histórico (Ya Aplicado):
                    </span>
                    <span className="font-bold text-base">
                      ${formatAmount(alreadyLinked.reduce((sum, p) => sum + (parseFloat(p.reconciled_amount || p.valor) || 0), 0))}
                    </span>
                  </div>
                </div>
              )}

              {/* Formulario según tipo de pago */}
              {['transferencia', 'pago_electronico', 'contraentrega', 'efectivo', 'contado', 'cliente_credito', 'credito'].includes(normalizePaymentMethod(order.payment_method)) ? (
                <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
                  <div className="flex flex-col">
                    {/* TITLE */}
                    <h2 className="text-xl font-bold flex items-center text-gray-800">
                      <Icons.CheckCircle className="w-6 h-6 mr-2 text-green-600" />
                      Validación (v5)
                    </h2>
                  </div>

                  <div className="p-6 space-y-6">

                    {/* --- SECCIÓN CONCILIACIÓN ONLINE (OPCIONAL) --- */}
                    {!['cliente_credito', 'credito'].includes(normalizePaymentMethod(order.payment_method)) && (
                      <div className="bg-gradient-to-br from-indigo-50 to-blue-50 p-4 rounded-xl border border-indigo-100 shadow-sm animate-fade-in">
                        <button
                          type="button"
                          onClick={() => setShowReconciliation(!showReconciliation)}
                          className="flex items-center justify-between w-full mb-3 focus:outline-none group"
                        >
                          <div className="flex items-center text-indigo-900 font-bold">
                            <Icons.Link2 className={`w-5 h-5 mr-2 transition-transform ${showReconciliation ? 'rotate-90' : ''}`} />
                            <span>Conciliación Online (Opcional)</span>
                            {!linkedMovements.length && <span className="ml-2 text-xs font-normal text-indigo-500 bg-indigo-100 px-2 py-0.5 rounded-full">Clic para buscar</span>}
                          </div>
                          {linkedMovements.length > 0 && (
                            <span className="text-xs font-bold text-green-600 bg-green-100 px-2 py-1 rounded flex items-center">
                              <Icons.Check className="w-3 h-3 mr-1" />
                              {linkedMovements.length} Vinculado{linkedMovements.length > 1 ? 's' : ''}
                            </span>
                          )}
                        </button>

                        {showReconciliation && (
                          <div className="space-y-3">
                            {/* Lista de Movimientos Vinculados (si hay) */}
                            {linkedMovements.length > 0 && (
                              <div className="space-y-2">
                                <div className="text-xs font-semibold text-gray-700 mb-1">
                                  Movimientos Vinculados ({linkedMovements.length}):
                                </div>
                                {linkedMovements.map((movement, idx) => (
                                  <div key={idx} className="bg-green-50 p-3 rounded-lg border border-green-200 flex justify-between items-center animate-fade-in">
                                    <div className="flex items-center flex-1">
                                      <div className="mr-2 bg-green-100 p-1.5 rounded-full">
                                        <Icons.Check className="w-4 h-4 text-green-600" />
                                      </div>
                                      <div className="flex-1">
                                        <div className="text-sm font-bold text-gray-800">
                                          ${formatAmount(movement.valor)}
                                        </div>
                                        <div className="text-xs text-gray-500 truncate max-w-[200px]">
                                          {movement.descripcion}
                                        </div>
                                        <div className="text-xs text-gray-400">
                                          {formatDate(movement.fecha)}
                                        </div>
                                      </div>
                                    </div>
                                    <button
                                      type="button"
                                      onClick={() => handleRemoveMovement(idx)}
                                      className="ml-2 p-1.5 text-red-500 hover:bg-red-100 rounded-md transition-colors"
                                      title="Quitar movimiento"
                                    >
                                      <Icons.X className="w-4 h-4" />
                                    </button>
                                  </div>
                                ))}

                                {/* Total Acumulado */}
                                <div className="bg-blue-600 text-white p-3 rounded-lg flex justify-between items-center font-bold shadow-md">
                                  <span className="text-sm">Total Acumulado:</span>
                                  <span className="text-lg">${formatAmount(linkedMovements.reduce((sum, m) => sum + m.valor, 0))}</span>
                                </div>
                              </div>
                            )}

                            {/* Buscador (siempre visible) */}
                            <div className="space-y-2">
                              <div className="text-xs font-semibold text-gray-700 mb-1">
                                {linkedMovements.length > 0 ? 'Agregar otro movimiento:' : 'Buscar movimiento:'}
                              </div>

                              <div className="relative">
                                <Icons.Search className="absolute left-3 top-2.5 text-gray-400 w-4 h-4" />
                                <input
                                  type="text"
                                  placeholder="Buscar por monto exacto (ej: 50000) o nombre..."
                                  value={movementSearch}
                                  onChange={(e) => setMovementSearch(e.target.value)}
                                  className="w-full pl-9 pr-4 py-2 border border-indigo-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white"
                                />
                              </div>

                              <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-lg bg-white shadow-inner custom-scrollbar">
                                {pendingMovements.length === 0 ? (
                                  <div className="p-4 text-center text-gray-400 text-xs">
                                    {loadingMovements ? <Icons.Loader2 className="w-4 h-4 animate-spin mx-auto mb-1" /> : 'No hay movimientos pendientes recientes.'}
                                  </div>
                                ) : (
                                  (() => {
                                    const filtered = pendingMovements.filter(m => {
                                      if (!movementSearch) return true;
                                      const searchLower = movementSearch.toLowerCase();
                                      const amountMatch = m.valor.toString().includes(searchLower);
                                      const descMatch = (m.descripcion || '').toLowerCase().includes(searchLower);
                                      return amountMatch || descMatch;
                                    });

                                    if (filtered.length === 0) {
                                      return <div className="p-4 text-center text-gray-400 text-xs">No se encontraron coincidencias.</div>;
                                    }

                                    return (
                                      <ul className="divide-y divide-gray-100">
                                        {filtered.map(movement => (
                                          <li
                                            key={movement.id}
                                            onClick={() => handleSelectMovement(movement)}
                                            className="p-3 text-xs cursor-pointer transition-colors hover:bg-indigo-50 flex justify-between items-center"
                                          >
                                            <div>
                                              <div className="font-bold text-gray-800">${formatAmount(movement.valor)}</div>
                                              <div className="text-gray-500 truncate max-w-[200px]">{movement.descripcion}</div>
                                            </div>
                                            <div className="text-right">
                                              <div className="text-gray-600 font-mono">{formatDate(movement.fecha)}</div>
                                            </div>
                                          </li>
                                        ))}
                                      </ul>
                                    );
                                  })()
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* --- SECCIÓN CRÉDITO DEL CLIENTE --- */}
                    {['cliente_credito', 'credito'].includes(normalizePaymentMethod(order.payment_method)) && (
                      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden animate-fade-in">
                        <div className="bg-blue-600 px-4 py-3">
                          <h3 className="text-white font-bold flex items-center text-sm">
                            <Icons.ShieldCheck className="w-4 h-4 mr-2" />
                            Información de Cartera
                          </h3>
                        </div>
                        <div className="p-4 space-y-4">
                          {loadingCredit ? (
                            <div className="flex flex-col items-center justify-center py-6 text-gray-500">
                              <Icons.Loader2 className="w-6 h-6 animate-spin mb-2" />
                              <span className="text-xs">Consultando base de datos...</span>
                            </div>
                          ) : customerCredit ? (
                            <>
                              <div className="grid grid-cols-1 gap-3">
                                <div className="p-3 bg-gray-50 rounded-lg border border-gray-100">
                                  <span className="text-[10px] uppercase font-bold text-gray-400 block mb-1">Cupo de Crédito</span>
                                  <span className="text-lg font-bold text-gray-900">${formatAmount(customerCredit.credit_limit)}</span>
                                </div>
                                <div className="p-3 bg-gray-50 rounded-lg border border-gray-100">
                                  <span className="text-[10px] uppercase font-bold text-gray-400 block mb-1">Saldo Actual (Siigo)</span>
                                  <span className="text-lg font-bold text-red-600">${formatAmount(customerCredit.current_balance)}</span>
                                </div>
                                <div className={`p-3 rounded-lg border ${customerCredit.available_credit >= parseFloat(order.total_amount) ? 'bg-green-50 border-green-100' : 'bg-red-50 border-red-100'}`}>
                                  <span className={`text-[10px] uppercase font-bold block mb-1 ${customerCredit.available_credit >= parseFloat(order.total_amount) ? 'text-green-600' : 'text-red-500'}`}>Cupo Disponible</span>
                                  <span className={`text-xl font-black block ${customerCredit.available_credit >= parseFloat(order.total_amount) ? 'text-green-700' : 'text-red-700'}`}>
                                    ${formatAmount(customerCredit.available_credit)}
                                  </span>
                                </div>
                              </div>

                              {customerCredit.available_credit < parseFloat(order.total_amount) && (
                                <div className="bg-red-100 border-l-4 border-red-500 p-3 rounded-r">
                                  <div className="flex">
                                    <Icons.AlertTriangle className="h-5 w-5 text-red-500 mr-2 flex-shrink-0" />
                                    <div>
                                      <p className="text-xs font-bold text-red-800">CUIDADO: Cupo Insuficiente</p>
                                      <p className="text-[10px] text-red-700">El pedido excede el disponible por ${formatAmount(parseFloat(order.total_amount) - customerCredit.available_credit)}.</p>
                                    </div>
                                  </div>
                                </div>
                              )}

                              {(customerCredit.status || '').toLowerCase() !== 'active' && (
                                <div className="bg-orange-100 border-l-4 border-orange-500 p-3 rounded-r">
                                  <div className="flex">
                                    <Icons.Lock className="h-5 w-5 text-orange-500 mr-2 flex-shrink-0" />
                                    <p className="text-xs font-bold text-orange-800 uppercase">CLIENTE CON CRÉDITO BLOQUEADO / INACTIVO</p>
                                  </div>
                                </div>
                              )}

                              <div className="pt-2">
                                <label className="flex items-center cursor-pointer group">
                                  <input
                                    type="checkbox"
                                    checked={formData.creditApproved}
                                    onChange={(e) => handleInputChange('creditApproved', e.target.checked)}
                                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                  />
                                  <span className="ml-2 text-xs font-bold text-gray-700 group-hover:text-blue-700 transition-colors">
                                    Confirmar aprobación de crédito manual
                                  </span>
                                </label>
                              </div>
                            </>
                          ) : (
                            <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg text-center">
                              <Icons.UserX className="w-8 h-8 text-yellow-500 mx-auto mb-2" />
                              <p className="text-xs font-bold text-yellow-800 uppercase">Cliente no configurado</p>
                              <p className="text-[10px] text-yellow-700 mt-1">Este cliente no tiene una hoja de crédito activa en la base de datos.</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Selector de Tipo de Efectivo (SOLO CARTERA + EFECTIVO + DOMICILIO) */}
                    {normalizePaymentMethod(order.payment_method) === 'efectivo' && isWalletValidator && isDomicile && (
                      <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 border border-green-200 rounded-lg mb-4">
                        <label className="block text-xs font-bold text-gray-700 uppercase tracking-wide mb-2 flex items-center">
                          <Icons.DollarSign className="w-4 h-4 mr-1 text-green-600" />
                          Tipo de Efectivo
                        </label>
                        <select
                          value={formData.cashType}
                          onChange={(e) => handleInputChange('cashType', e.target.value)}
                          className="w-full px-3 py-2 border border-green-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 bg-white text-sm font-medium text-gray-800"
                        >
                          <option value="efectivo">Efectivo Bodega</option>
                          <option value="efectivo_ruta_domicilio">Efectivo Ruta Domicilio (Mensajero)</option>
                        </select>
                        <p className="text-xs text-green-700 mt-2 flex items-start">
                          <Icons.Info className="w-3 h-3 mr-1 mt-0.5 flex-shrink-0" />
                          <span>
                            <strong>Efectivo Bodega:</strong> Dinero recibido directamente en bodega.
                            <strong className="ml-2">Efectivo Ruta:</strong> Dinero que trae el mensajero de la ruta.
                          </span>
                        </p>
                      </div>
                    )}

                    {/* Responsabilidad Checkbox (SOLO PARA TRANSFERENCIA, EFECTIVO, CONTRAENTREGA) */}
                    {['transferencia', 'efectivo', 'contraentrega'].includes(normalizePaymentMethod(order.payment_method)) && isWalletValidator && (
                      <div className="bg-orange-50 p-4 border border-orange-200 rounded-lg mb-4">
                        <label className="flex items-start cursor-pointer group">
                          <div className="flex items-center h-5">
                            <input
                              type="checkbox"
                              id="responsibility-checkbox"
                              checked={formData.responsibilityChecked}
                              onChange={(e) => handleInputChange('responsibilityChecked', e.target.checked)}
                              className="w-5 h-5 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                            />
                          </div>
                          <div className="ml-3 text-sm">
                            <span className="font-bold text-gray-800">Declaro bajo mi responsabilidad</span>
                            <p className="text-gray-600">que he verificado en el portal bancario que este dinero ingresó correctamente.</p>
                          </div>
                        </label>
                      </div>
                    )}

                    {/* Nueva Carga de Evidencia Bancaria - OCULTA PARA CRÉDITO */}
                    {!['cliente_credito', 'credito'].includes(normalizePaymentMethod(order.payment_method)) && (
                      <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                        <label className="block text-xs font-bold text-blue-900 mb-1 flex items-center">
                          <Icons.UploadCloud className="w-4 h-4 mr-1" />
                          Evidencia de Banco {existingEvidences.length === 0 ? '(Obligatorio)' : '(Opcional, ya existen comprobantes)'}
                        </label>
                        <p className="text-xs text-blue-700 mb-2">
                          Sube o pega (Ctrl+V) aquí el pantallazo de tu portal bancario para confirmar el ingreso.
                        </p>

                        <MultiplePaymentEvidenceUpload
                          orderId={order?.id}
                          readOnly={false}
                          showExistingEvidences={false} // NO mostrar lista vieja, solo uploader
                          onUploadComplete={() => {
                            setSessionUploads(prev => prev + 1);
                            setRefreshKey(prev => prev + 1); // Actualizar lista izquierda
                          }}
                        />
                        {sessionUploads > 0 && (
                          <div className="mt-2 flex items-center text-xs font-bold text-green-600 bg-green-50 p-2 rounded border border-green-200">
                            <Icons.Check className="w-4 h-4 mr-1" />
                            ¡Evidencia cargada! ({sessionUploads})
                          </div>
                        )}
                      </div>
                    )}

                    {/* Inputs de Monto para Transferencia (SINGLE) - OCULTOS PARA CRÉDITO */}
                    {!['cliente_credito', 'credito'].includes(normalizePaymentMethod(order.payment_method)) && (
                      <div className={`grid grid-cols-1 gap-4 ${!isWalletValidator ? 'hidden' : ''}`}>
                        <div>
                          <label className="block text-xs font-bold text-gray-700 uppercase tracking-wide mb-1">
                            Banco Destino
                          </label>
                          <select
                            value={formData.bankName}
                            onChange={(e) => handleInputChange('bankName', e.target.value)}
                            className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm ${linkedMovements.length > 0 ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : 'bg-white'}`}
                            required
                            disabled={linkedMovements.length > 0} // Bloquear si viene de conciliación
                          >
                            <option value="">Seleccionar...</option>
                            <option value="bancolombia">Bancolombia</option>
                            <option value="mercadopago">MercadoPago</option>
                            <option value="bold">Bold</option>
                          </select>
                        </div>
                      </div>
                    )}

                    {/* Validación visual del monto - OCULTA PARA CRÉDITO */}
                    {!['cliente_credito', 'credito'].includes(normalizePaymentMethod(order.payment_method)) && (
                      <div className={`p-4 rounded-xl border ${singlePaymentWithinTolerance
                        ? 'bg-green-50 border-green-200'
                        : 'bg-red-50 border-red-200'
                        }`}>
                        <div className="flex flex-col">
                          <label className="block text-xs font-bold text-gray-700 uppercase tracking-wide mb-1">
                            Monto Transferido
                          </label>
                          <div className="relative">
                            <span className="absolute left-3 top-2 text-gray-500">$</span>
                            <input
                              type="number"
                              value={linkedMovements.length > 0
                                ? linkedMovements.reduce((sum, m) => sum + Number(m.valor), 0)
                                : formData.paymentAmount}
                              onChange={(e) => handleInputChange('paymentAmount', e.target.value)}
                              className={`bg-white focus:ring-blue-500 focus:border-blue-500 block w-full pl-7 px-3 py-3 sm:text-lg border-gray-300 rounded-md font-bold ${linkedMovements.length > 0 ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : 'text-gray-900'
                                }`}
                              placeholder="0"
                              readOnly={linkedMovements.length > 0}
                            />
                          </div>

                          {/* Feedback de saldo */}
                          {(() => {
                            const currentAmount = linkedMovements.length > 0
                              ? linkedMovements.reduce((sum, m) => sum + Number(m.valor), 0)
                              : parseFloat(formData.paymentAmount || 0);
                            const remaining = Math.max(0, orderTotal - currentAmount);
                            return (
                              <>
                                {remaining > 0 && remaining < orderTotal ? (
                                  <div className="mt-2 flex items-center text-sm text-yellow-700 bg-yellow-50 p-3 rounded border border-yellow-200 animate-fade-in">
                                    <Icons.AlertTriangle className="w-5 h-5 mr-2 flex-shrink-0" />
                                    <span>
                                      Pago Parcial: Se cobrará el restante (<strong>${formatAmount(remaining)}</strong>) al momento de la entrega.
                                    </span>
                                  </div>
                                ) : remaining === 0 ? (
                                  <div className="mt-2 flex items-center text-sm text-green-700 bg-green-50 p-3 rounded border border-green-200 animate-fade-in">
                                    <Icons.CheckCircle className="w-5 h-5 mr-2 flex-shrink-0" />
                                    <span>Pago Completo Cubierto</span>
                                  </div>
                                ) : null}
                              </>
                            );
                          })()}
                        </div>
                      </div>
                    )}


                    {/* Soportes Existentes (API) - OCULTO: duplicado con sidebar izquierdo */}
                    {/* {existingEvidences.length > 0 && (
                      <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm mt-4 mb-4">
                        <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2 flex items-center">
                          <Icons.Image className="w-3 h-3 mr-2" /> Evidencia Previa
                        </h4>
                        <div className="grid grid-cols-2 gap-2">
                          {existingEvidences.map((url, idx) => (
                            <div key={idx} className="relative group cursor-pointer" onClick={() => setPreviewImage(url)}>
                              <img
                                src={url.startsWith('http') ? url : `/api/${url.replace(/^\//, '')}`}
                                alt={`Soporte ${idx + 1}`}
                                className="w-full h-24 object-cover rounded-lg border border-gray-200 hover:border-emerald-500 transition-colors"
                                onError={(e) => {
                                  if (!e.target.src.includes('/api/public/evidence')) {
                                    const filename = url.split('/').pop();
                                    e.target.src = `/api/public/evidence/${filename}/content`;
                                  }
                                }}
                              />
                              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors rounded-lg flex items-center justify-center">
                                <Icons.ZoomIn className="text-white opacity-0 group-hover:opacity-100 drop-shadow-md" />
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )} */}



                    {/* Notas de Validación (Siempre visibles) */}
                    <div className="space-y-2">
                      <label className="block text-xs font-bold text-gray-700 uppercase tracking-wide mb-1 flex items-center">
                        <Icons.MessageSquare className="w-4 h-4 mr-1 text-gray-400" />
                        Notas / Motivo del Cambio
                      </label>
                      <textarea
                        value={formData.validationNotes}
                        onChange={(e) => handleInputChange('validationNotes', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                        placeholder="Ej: El dinero ya se ve reflejado en Bancolombia..."
                        rows="3"
                      />
                    </div>

                    {/* Footer Actions (Siempre visibles) */}
                    <div className="pt-6 border-t border-gray-200 flex justify-between items-center rounded-b-lg">
                      {/* Left: Return to Billing button */}
                      <div>
                        {(hasRole('cartera') || hasRole('admin')) && order?.status === 'revision_cartera' && (
                          <button
                            type="button"
                            onClick={() => setShowReturnToBilling(true)}
                            disabled={loading}
                            className="px-4 py-2 text-orange-700 bg-orange-50 border border-orange-300 rounded-lg hover:bg-orange-100 font-medium shadow-sm flex items-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Devolver este pedido a Facturación para corrección"
                          >
                            <Icons.ArrowLeft className="w-4 h-4" />
                            Devolver a Facturación
                          </button>
                        )}
                      </div>

                      {/* Right: Standard action buttons */}
                      <div className="flex space-x-3">
                        <button
                          type="button"
                          onClick={onClose}
                          className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 font-medium shadow-sm"
                          disabled={loading}
                        >
                          Cancelar
                        </button>
                        <button
                          type="button"
                          onClick={() => handleValidate('approved')}
                          disabled={approveDisabled}
                          className={`px-6 py-2 text-white rounded-lg shadow-md font-bold flex items-center ${(approveDisabled || loading)
                            ? 'bg-gray-400 cursor-not-allowed'
                            : (() => {
                              const pmNorm = normalizePaymentMethod(order?.payment_method);
                              const isTransfer = pmNorm === 'transferencia';
                              const isElectronic = pmNorm === 'pago_electronico' || pmNorm === 'mercado_pago';
                              const bankLower = String(formData.bankName || '').toLowerCase();
                              const isSupportedElectronic = isElectronic && (bankLower.includes('bold') || bankLower.includes('mercado') || bankLower.includes('mp') || bankLower.includes('mercado_pago'));
                              // FALLBACK: Si no hay siigo_invoice_number, usar order_number si parece factura (tiene guion)
                              const hasInvoice = (order?.siigo_invoice_number && order.siigo_invoice_number.includes('-')) || (order?.order_number && order.order_number.includes('-'));

                              if (isWalletValidator && hasInvoice && (isTransfer || isSupportedElectronic)) {
                                return 'bg-gradient-to-r from-green-600 to-orange-500 hover:from-green-700 hover:to-orange-600 border border-transparent';
                              }
                              return 'bg-green-600 hover:bg-green-700';
                            })()
                            }`}
                          title={(() => {
                            const pmNorm = normalizePaymentMethod(order?.payment_method);
                            const isTransfer = pmNorm === 'transferencia';
                            const isElectronic = pmNorm === 'pago_electronico' || pmNorm === 'mercado_pago';
                            const bankLower = String(formData.bankName || '').toLowerCase();
                            const isSupportedElectronic = isElectronic && (bankLower.includes('bold') || bankLower.includes('mercado') || bankLower.includes('mp') || bankLower.includes('mercado_pago'));
                            // FALLBACK
                            const hasInvoice = (order?.siigo_invoice_number && order.siigo_invoice_number.includes('-')) || (order?.order_number && order.order_number.includes('-'));

                            if (isWalletValidator && hasInvoice && (isTransfer || isSupportedElectronic)) {
                              return 'Se creará automáticamente el Recibo de Caja (RC) en Siigo';
                            }
                            return '';
                          })()}
                        >
                          {loading ? <Icons.Loader2 className="animate-spin w-5 h-5 mr-2" /> : <Icons.CheckCircle className="w-5 h-5 mr-2" />}
                          {(() => {
                            const pmNorm = normalizePaymentMethod(order?.payment_method);
                            const isTransfer = pmNorm === 'transferencia';
                            const isElectronic = pmNorm === 'pago_electronico' || pmNorm === 'mercado_pago';
                            const bankLower = String(formData.bankName || '').toLowerCase();
                            const isSupportedElectronic = isElectronic && (bankLower.includes('bold') || bankLower.includes('mercado') || bankLower.includes('mp') || bankLower.includes('mercado_pago'));
                            // FALLBACK
                            const hasInvoice = (order?.siigo_invoice_number && order.siigo_invoice_number.includes('-')) || (order?.order_number && order.order_number.includes('-'));

                            if (isWalletValidator && hasInvoice && (isTransfer || isSupportedElectronic)) {
                              return 'Aprobar y Crear RC';
                            }
                            return isBillingValidator ? 'Registrar Pago para Validación' : 'Aprobar Validación';
                          })()}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          </div >
        </div >
      </div >

      {/* Return to Billing Modal */}
      < ReturnToBillingModal
        isOpen={showReturnToBilling}
        onClose={() => setShowReturnToBilling(false)}
        order={order}
        onSuccess={handleReturnToBillingSuccess}
      />

      {/* Modal de confirmación de sobrepago */}
      {showOverpaymentConfirmation && overpaymentDetails && (
        <div className="fixed inset-0 z-[70] overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>

            {/* This element is to trick the browser into centering the modal contents. */}
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100 sm:mx-0 sm:h-10 sm:w-10">
                    <Icons.AlertTriangle className="h-6 w-6 text-yellow-600" aria-hidden="true" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                      Confirmar Sobrepago
                    </h3>
                    <div className="mt-2 text-sm text-gray-500 space-y-3">
                      <div className="bg-red-50 p-2 text-red-700 text-xs rounded border border-red-200">
                        <strong>ADVERTENCIA:</strong> El valor ingresado es mayor al valor del pedido.
                      </div>
                      <div className="bg-gray-50 p-3 rounded-md border border-gray-200">
                        <div className="flex justify-between">
                          <span>Valor Pedido:</span>
                          <span className="font-bold">${formatAmount(overpaymentDetails.orderTotal)}</span>
                        </div>
                        <div className="flex justify-between mt-1">
                          <span>Valor Transferido:</span>
                          <span className="font-bold text-blue-600">${formatAmount(overpaymentDetails.amount)}</span>
                        </div>
                        <div className="flex justify-between mt-2 pt-2 border-t border-gray-300 text-yellow-700 font-bold">
                          <span>Exceso (Saldo a Favor):</span>
                          <span>+${formatAmount(overpaymentDetails.excess)}</span>
                        </div>
                      </div>
                      <p className="font-bold text-gray-800">
                        ¿El cliente tiene conocimiento de este saldo a favor?
                      </p>
                      <p className="text-xs">
                        Al confirmar, se creará el Recibo de Caja por el valor total transferido ($ {formatAmount(overpaymentDetails.amount)}).
                        El saldo a favor quedará registrado en SIIGO automáticamente.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={confirmOverpayment}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-yellow-600 text-base font-medium text-white hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Sí, Aprobar y Crear RC
                </button>
                <button
                  type="button"
                  onClick={() => setShowOverpaymentConfirmation(false)}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mixtures Validation Modal - DISABLED in Cartera, only in Facturación */}
      {/* <MixturesValidationModal ... /> */}

    </div >

  );
};

export default WalletValidationModal;


